import { Mic, Send, Stop } from '@mui/icons-material';
import { IconButton, InputAdornment, styled, TextField } from '@mui/material';
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useSpeechRecognition } from '../../../hooks/useSpeechRecognition';
import { isMobileDevice } from '../../../util/mobile';
import { ChatInputZIndex } from './style-defs';

const StyledTextField = styled(TextField)`
  .MuiOutlinedInput-root {
    border-radius: ${({ theme }) => theme.spacing(2)};

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.palette.primary.main};
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border-color: ${({ theme }) => theme.palette.primary.main};
    }
  }

  .MuiOutlinedInput-input {
    padding: ${({ theme }) => theme.spacing(1.5, 2)};
    max-height: 100px;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${({ theme }) => theme.palette.grey[300]};
      border-radius: 2px;
    }
  }
`;

const SendButton = styled(IconButton)<{ disabled: boolean }>`
  width: 40px;
  height: 40px;
  margin-right: ${({ theme }) => theme.spacing(0.5)};
  background: ${({ theme, disabled }) => (disabled ? theme.palette.grey[200] : theme.palette.primary.main)};
  color: ${({ theme, disabled }) =>
    disabled ? theme.palette.grey[400] : theme.palette.primary.contrastText};

  &:hover {
    background: ${({ theme, disabled }) => (disabled ? theme.palette.grey[200] : theme.palette.primary.dark)};
  }

  &:disabled {
    background: ${({ theme }) => theme.palette.grey[200]};
    color: ${({ theme }) => theme.palette.grey[400]};
  }
`;

interface MessageInputProps {
  isCancelling: boolean;
  isDisabled: boolean;
  isReceiving: boolean;
  onCancelGeneration: () => void;
  onSendMessage: (message: string) => void;
  placeholder?: string;
}

export function MessageInput({
  isCancelling,
  isDisabled,
  isReceiving,
  onCancelGeneration,
  onSendMessage,
  placeholder = 'Type your message...',
}: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [shouldFocus, setShouldFocus] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const onSpeechResult = useCallback(
    (transcript: string) => {
      setMessage(transcript);
    },
    [setMessage]
  );

  const onSpeechError = useCallback((error: string) => {
    console.error('Speech recognition error:', error);
  }, []);

  const { isRecording, hasPermission, toggleRecording, stopRecording } = useSpeechRecognition({
    onResult: onSpeechResult,
    onError: onSpeechError,
  });

  const onMessageTyped = (event: ChangeEvent<HTMLInputElement>) => {
    stopRecording();
    setMessage(event.target.value);
  };

  const isMobile = isMobileDevice();
  useEffect(() => {
    if (shouldFocus && !isReceiving && !isDisabled) {
      if (!isMobile) {
        inputRef.current?.focus();
      }
    }
  }, [shouldFocus, isReceiving, isDisabled, isMobile]);

  const handleSend = () => {
    if (!canSend) {
      return;
    }
    onSendMessage(message.trim());
    setMessage('');
    setShouldFocus(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const canSend = message.trim().length > 0 && !isReceiving && !isDisabled && !isRecording;
  const showVoiceButton = !message.trim() && hasPermission && !isReceiving && !isDisabled;

  const getButtonIcon = () => {
    if (isReceiving) {
      return <Stop />;
    }
    if (isRecording) {
      return <Stop />;
    }
    if (showVoiceButton) {
      return <Mic />;
    }
    return <Send />;
  };

  const getButtonAriaLabel = () => {
    if (isReceiving) {
      return 'Cancel generation';
    }
    if (isRecording) {
      return 'Stop recording';
    }
    if (showVoiceButton) {
      return 'Start voice recording';
    }
    return 'Send message';
  };

  const isButtonDisabled = (): boolean => {
    if (isReceiving) {
      return isCancelling;
    }
    if (isRecording) {
      return false;
    }
    if (showVoiceButton) {
      return isDisabled;
    }
    return !canSend || isDisabled;
  };

  const handleButtonClick = () => {
    if (isReceiving) {
      onCancelGeneration();
      return;
    }
    if (isRecording || showVoiceButton) {
      toggleRecording();
    } else {
      handleSend();
    }
  };

  return (
    <StyledTextField
      fullWidth
      multiline
      maxRows={4}
      variant='outlined'
      placeholder={placeholder}
      value={message}
      onChange={onMessageTyped}
      onKeyDown={handleKeyDown}
      size='medium'
      inputRef={inputRef}
      autoFocus={!isMobile}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position='end'>
              <SendButton
                onClick={handleButtonClick}
                disabled={isButtonDisabled()}
                aria-label={getButtonAriaLabel()}
              >
                {getButtonIcon()}
              </SendButton>
            </InputAdornment>
          ),
          sx: {
            zIndex: ChatInputZIndex,
          },
        },
      }}
    />
  );
}
